import React, { useEffect, useState, useCallback } from "react";
import { useLiveRefresh } from "@/hooks/useLiveRefresh";
import api from "@/lib/api";
import { formatINRFull, formatNumber, formatCr, formatPct } from "@/lib/format";
import { Search, ChevronLeft, ChevronRight, GitPullRequestArrow, CheckCircle2, XCircle, Clock, Sparkles, Download, ShieldX } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell, LabelList } from "recharts";
import { Kpi, ChartCard, Chart, PALETTE, tooltipStyle } from "@/components/charts";
import { LoanFileDrawer } from "@/components/LoanFile";

const STATUS_COLORS = {
  Approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Rejected: "bg-red-50 text-[#b3122a] border-red-200",
  "Under Review": "bg-blue-50 text-blue-700 border-blue-200",
  "Field Visit": "bg-amber-50 text-amber-700 border-amber-200",
  "Incomplete Application": "bg-slate-100 text-slate-600 border-slate-200",
  "For Future": "bg-purple-50 text-purple-700 border-purple-200",
};

function Status({ s }) {
  return <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border whitespace-nowrap ${STATUS_COLORS[s] || "bg-slate-100 text-slate-600 border-slate-200"}`}>{s}</span>;
}

function Select({ value, onChange, options, label, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="px-3 py-2.5 rounded-sm border border-slate-300 bg-white text-sm font-medium text-slate-700 focus:border-[#b3122a] focus:ring-2 focus:ring-[#b3122a]/20 outline-none transition cursor-pointer">
      <option value="all">{label}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

export default function Pipeline() {
  const [version] = useLiveRefresh();
  const [summary, setSummary] = useState(null);
  const [charts, setCharts] = useState(null);
  const [filters, setFilters] = useState({ statuses: [], loan_types: [] });
  const [data, setData] = useState({ items: [], total: 0, page: 1 });
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [loanType, setLoanType] = useState("all");
  const [includeTest, setIncludeTest] = useState(false);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [detailId, setDetailId] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/pipeline/summary", { params: { include_test: includeTest } }),
      api.get("/pipeline/charts", { params: { include_test: includeTest } }),
      api.get("/pipeline/filters"),
    ])
      .then(([s, c, f]) => { setSummary(s.data); setCharts(c.data); setFilters(f.data); })
      .catch((e) => setError(e.response?.data?.detail || e.message));
  }, [includeTest, version]);

  const fetchApplicants = useCallback(() => {
    setLoading(true);
    api.get("/pipeline/applicants", { params: { search, status, loan_type: loanType, include_test: includeTest, page, page_size: 20 } })
      .then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [search, status, loanType, includeTest, page, version]);

  useEffect(() => { const t = setTimeout(fetchApplicants, 300); return () => clearTimeout(t); }, [fetchApplicants]);
  useEffect(() => { setPage(1); }, [search, status, loanType, includeTest]);

  const exportCsv = async () => {
    const res = await api.get("/pipeline/export", { params: { search, status, loan_type: loanType, include_test: includeTest }, responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url; a.download = "pipeline.csv"; a.click();
    window.URL.revokeObjectURL(url);
  };

  if (error) return <div className="p-8 text-[#b3122a]" data-testid="pipeline-error">Failed to load: {error}</div>;
  if (!summary || !charts) return <div className="p-8 text-slate-400" data-testid="pipeline-loading">Loading pipeline…</div>;

  const totalPages = Math.max(1, Math.ceil(data.total / 20));

  return (
    <div className="p-6 lg:p-8" data-testid="pipeline-page">
      <header className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-[#1a1a1b]">Origination Pipeline (LOS)</h1>
          <p className="text-slate-500 mt-1">
            {formatNumber(summary.total_applications)} {includeTest ? "applications (incl. test)" : "clean applications"} · {formatNumber(summary.quarantined)} quarantined
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm font-semibold text-slate-600 cursor-pointer select-none" data-testid="include-test-toggle">
            <input type="checkbox" checked={includeTest} onChange={(e) => setIncludeTest(e.target.checked)} className="h-4 w-4 accent-[#b3122a]" />
            <ShieldX size={15} className="text-slate-400" /> Include test / incomplete
          </label>
          <button onClick={exportCsv} data-testid="export-csv-btn" className="flex items-center gap-1.5 px-3 py-2 rounded-sm bg-[#b3122a] text-white text-sm font-semibold hover:bg-[#8c0d20] transition"><Download size={15} /> Export</button>
        </div>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
        <Kpi icon={GitPullRequestArrow} label="Total Applications" value={formatNumber(summary.total_applications)} sub={`${formatNumber(summary.active_pipeline)} active in funnel`} tone="blue" delay={0} testid="kpi-total" highlight />
        <Kpi icon={CheckCircle2} label="Approved" value={formatNumber(summary.approved)} sub={`Sanctioned ${formatCr(summary.approved_value)}`} tone="green" delay={60} testid="kpi-approved" />
        <Kpi icon={Sparkles} label="Approval Rate" value={formatPct(summary.approval_rate)} sub="Approved / decided" tone="amber" delay={120} testid="kpi-rate" />
        <Kpi icon={Clock} label="In Review / Visit" value={formatNumber(summary.under_review + summary.field_visit)} sub={`${formatNumber(summary.for_future)} parked for future`} tone="purple" delay={180} testid="kpi-review" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-7">
        <ChartCard title="Application Funnel" subtitle="Active origination stages" testid="chart-funnel">
          <Chart height={280}>
            <BarChart data={charts.funnel} layout="vertical" margin={{ left: 20, right: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef0f5" horizontal={false} />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: "#64748b" }} width={100} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="count" radius={[0, 6, 6, 0]} name="Applications" barSize={36}>
                {charts.funnel.map((d, i) => <Cell key={d.name} fill={PALETTE[i % PALETTE.length]} />)}
                <LabelList dataKey="count" position="right" style={{ fontSize: 12, fill: "#64748b" }} />
              </Bar>
            </BarChart>
          </Chart>
        </ChartCard>

        <ChartCard title="Demand by Loan Type" subtitle="Applications by product requested" testid="chart-loantype">
          <Chart height={280}>
            <BarChart data={charts.by_loan_type} margin={{ left: -5, right: 10 }} onClick={(e) => { const v = e?.activeLabel?.toLowerCase(); if (v && filters.loan_types.includes(v)) setLoanType(v); }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef0f5" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} interval={0} angle={-15} textAnchor="end" height={60} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={40} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="count" fill="#1f6feb" radius={[6, 6, 0, 0]} name="Applications" barSize={34} cursor="pointer" />
            </BarChart>
          </Chart>
        </ChartCard>
      </div>

      {/* Applicants table */}
      <div className="rf-card p-4 mb-5">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="pipeline-search"
              placeholder="Search company or applicant no…"
              className="w-full pl-10 pr-4 py-2.5 rounded-sm border border-slate-300 text-sm focus:border-[#b3122a] focus:ring-2 focus:ring-[#b3122a]/20 outline-none transition" />
          </div>
          <Select value={status} onChange={setStatus} options={filters.statuses.map((s) => s)} label="All statuses" testid="filter-status" />
          <Select value={loanType} onChange={setLoanType} options={filters.loan_types} label="All loan types" testid="filter-loantype" />
        </div>
        {(status !== "all" || loanType !== "all") && (
          <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-100" data-testid="pipeline-active-filters">
            <span className="text-xs font-semibold text-slate-400 self-center">Filtered:</span>
            {status !== "all" && <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#b3122a]/10 border border-[#b3122a]/20 text-xs font-semibold text-[#b3122a]">{status}<button onClick={() => setStatus("all")} className="hover:text-[#8c0d20]">×</button></span>}
            {loanType !== "all" && <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#b3122a]/10 border border-[#b3122a]/20 text-xs font-semibold text-[#b3122a]">{loanType}<button onClick={() => setLoanType("all")} className="hover:text-[#8c0d20]">×</button></span>}
          </div>
        )}
      </div>

      <div className="rf-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="pipeline-table">
            <thead>
              <tr className="bg-[#111111] text-slate-300">
                <th className="px-4 py-3 font-semibold text-xs uppercase tracking-wide text-left">Applicant #</th>
                <th className="px-4 py-3 font-semibold text-xs uppercase tracking-wide text-left">Company</th>
                <th className="px-4 py-3 font-semibold text-xs uppercase tracking-wide text-right">Applied</th>
                <th className="px-4 py-3 font-semibold text-xs uppercase tracking-wide text-right">Tenor</th>
                <th className="px-4 py-3 font-semibold text-xs uppercase tracking-wide text-left">Loan Type</th>
                <th className="px-4 py-3 font-semibold text-xs uppercase tracking-wide text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>}
              {!loading && data.items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400" data-testid="pipeline-empty">No applications match.</td></tr>}
              {!loading && data.items.map((a) => (
                <tr key={a.id} onClick={() => setDetailId(a.id)} className="rf-table-row border-t border-slate-100 cursor-pointer" data-testid={`applicant-row-${a.applicant_no}`}>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{a.applicant_no}</td>
                  <td className="px-4 py-3 font-semibold text-[#1a1a1b]">{a.company}</td>
                  <td className="px-4 py-3 text-right text-slate-600 whitespace-nowrap" data-testid={`legacy-pipeline-amount-${a.id}`}>{formatINRFull(a.applied_amount)}</td>
                  <td className="px-4 py-3 text-right text-slate-600">{a.tenor || "—"} mo</td>
                  <td className="px-4 py-3 text-slate-600">{a.loan_type}</td>
                  <td className="px-4 py-3"><Status s={a.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
          <span className="text-sm text-slate-500">Page {data.page} of {totalPages} · {formatNumber(data.total)} applications</span>
          <div className="flex gap-2">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} data-testid="page-prev" className="px-3 py-1.5 rounded-sm border border-slate-300 text-sm font-medium text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition flex items-center gap-1"><ChevronLeft size={16} /> Prev</button>
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} data-testid="page-next" className="px-3 py-1.5 rounded-sm border border-slate-300 text-sm font-medium text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition flex items-center gap-1">Next <ChevronRight size={16} /></button>
          </div>
        </div>
      </div>

      {detailId && <LoanFileDrawer id={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}
